/**
 * Оператор → вкладка «Bounty»: личный QR-код для добавления людей в Юнифанс.
 * Гость сканирует → попадает в Юнифанс, и мы записываем, что его привёл ИМЕННО этот оператор
 * (client_tg_link.recruited_by_staff → фуксия-точка в графе DC). Это НЕ подписка в канал —
 * индивидуальный допуск. Источник — GET /api/recruit/qr (staff-auth, self-scoped).
 */
import { useEffect, useState, type CSSProperties, type ReactElement } from "react";
import QRCode from "qrcode";
import { api } from "../../api/client";

type RecruitQr = { link: string; token: string; recruited: number; booked: number };

const FUCHSIA = "#ff3ea5";
const card: CSSProperties = { border: "1px solid var(--surface-3)", borderRadius: 14, background: "var(--surface-1)", padding: 16 };
const stat: CSSProperties = { flex: 1, textAlign: "center", padding: "10px 6px", border: "1px solid var(--surface-3)", borderRadius: 12, background: "var(--surface-2)" };

export function BountyTab(): ReactElement {
  const [data, setData] = useState<RecruitQr | null>(null);
  const [qr, setQr] = useState("");
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api<RecruitQr>("/api/recruit/qr").then(setData).catch((e: unknown) => setErr(e instanceof Error ? e.message : "Ошибка"));
  }, []);
  useEffect(() => {
    if (!data?.link) return;
    let live = true;
    QRCode.toDataURL(data.link, { width: 340, margin: 1, errorCorrectionLevel: "H", color: { dark: "#0a0a0f", light: "#ffffff" } })
      .then((v) => { if (live) setQr(v); }).catch(() => { if (live) setQr(""); });
    return () => { live = false; };
  }, [data?.link]);

  const conv = data && data.recruited > 0 ? Math.round((data.booked / data.recruited) * 100) : 0;

  return (
    <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 12, maxWidth: 520, margin: "0 auto" }}>
      <div>
        <h2 style={{ margin: "0 0 4px", fontSize: 19, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ width: 12, height: 12, borderRadius: "50%", background: FUCHSIA, boxShadow: `0 0 10px ${FUCHSIA}` }} /> Мой QR — привожу в Юнифанс
        </h2>
        <p style={{ margin: 0, fontSize: 13, color: "var(--text-2)", lineHeight: 1.45 }}>
          Покажи этот QR гостю. Он отсканирует — попадёт в Юнифанс, а система запишет, что его привёл <b>ты</b>.
          Это не подписка в канал, а личный допуск. Кто из твоих людей потом бронирует — видно в статистике ниже.
        </p>
      </div>

      {err ? <div style={{ ...card, color: "#ff6b6b" }}>Не удалось получить QR: {err}</div> : null}

      <div style={{ ...card, display: "flex", flexDirection: "column", alignItems: "center", gap: 12, borderColor: FUCHSIA }}>
        {qr ? <img src={qr} alt="Личный QR оператора" style={{ width: "min(300px, 78vw)", borderRadius: 12, boxShadow: `0 0 0 4px ${FUCHSIA}22` }} />
          : <div style={{ height: 240, display: "grid", placeItems: "center", color: "var(--text-3)" }}>Генерация QR…</div>}
        {data?.link ? (
          <>
            <code style={{ width: "100%", wordBreak: "break-all", fontSize: 11, color: "var(--text-3)", textAlign: "center" }}>{data.link}</code>
            <button type="button" onClick={() => {
              navigator.clipboard?.writeText(data.link).then(() => { setCopied(true); window.setTimeout(() => setCopied(false), 1400); }).catch(() => {});
            }} style={{ padding: "9px 18px", borderRadius: 10, border: "none", background: FUCHSIA, color: "#12060d", fontWeight: 800, fontSize: 14, cursor: "pointer" }}>
              {copied ? "Скопировано ✓" : "Копировать ссылку"}
            </button>
          </>
        ) : null}
      </div>

      <div style={{ display: "flex", gap: 10 }}>
        <div style={stat}>
          <div style={{ fontSize: 26, fontWeight: 900, color: FUCHSIA, lineHeight: 1 }}>{data?.recruited ?? "—"}</div>
          <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 3 }}>привёл в Юнифанс</div>
        </div>
        <div style={stat}>
          <div style={{ fontSize: 26, fontWeight: 900, color: "var(--green, #3ad29f)", lineHeight: 1 }}>{data?.booked ?? "—"}</div>
          <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 3 }}>из них забронировали</div>
        </div>
        <div style={stat}>
          <div style={{ fontSize: 26, fontWeight: 900, color: "var(--text-1)", lineHeight: 1 }}>{data ? `${conv}%` : "—"}</div>
          <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 3 }}>конверсия в бронь</div>
        </div>
      </div>
    </div>
  );
}
